import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'
import { Card } from './card'
export function dataListItemStyles({
  selected = false,
  emphasized = false,
  dragging = false,
  inset = 'rounded',
  className,
}: {
  selected?: boolean
  emphasized?: boolean
  dragging?: boolean
  inset?: 'rounded' | 'flush'
  className?: string
} = {}) {
  return cn(
    'flex min-w-0 items-start gap-3 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-ring',
    inset === 'flush'
      ? 'px-5 py-4 focus-visible:-outline-offset-2 [[data-slot=card]>ul:first-child>li:first-child>&]:rounded-t-panel [[data-slot=card]>ul:last-child>li:last-child>&]:rounded-b-panel'
      : 'rounded-control border border-transparent p-3.5 focus-visible:outline-offset-2',
    selected && 'border-action/25 bg-accent/50',
    emphasized && 'rounded-l-none border-l-4 border-l-action bg-accent/50',
    !selected && !emphasized && 'hover:bg-muted/60',
    dragging && 'opacity-60 shadow-md',
    className,
  )
}
export function DataListPanel({ className, ...props }: ComponentProps<typeof Card>) {
  return <Card {...props} padding="none" className={cn('overflow-hidden', className)} />
}
export function DataListHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="data-list-header"
      className={cn(
        'hidden items-center gap-4 border-b border-border bg-muted/35 px-5 py-3 text-xs font-semibold text-muted-foreground lg:grid',
        className,
      )}
    />
  )
}
export function DataListPanelBody({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} data-slot="data-list-panel-body" className={cn('p-5', className)} />
}
export function DataListPanelFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="data-list-panel-footer"
      className={cn('border-t border-border p-5', className)}
    />
  )
}
export function DataList({
  className,
  appearance = 'stacked',
  ...props
}: ComponentProps<'ul'> & { appearance?: 'stacked' | 'divided' }) {
  return (
    <ul
      {...props}
      data-slot="data-list"
      className={cn(
        'min-w-0 list-none',
        appearance === 'divided' ? 'divide-y divide-border' : 'space-y-2',
        className,
      )}
    />
  )
}
export function DataListItem({ className, ...props }: ComponentProps<'li'>) {
  return <li {...props} data-slot="data-list-item" className={cn('min-w-0', className)} />
}
export function DataListContent({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} className={cn('min-w-0 flex-1 space-y-1 break-words', className)} />
}
export function DataListMeta({ className, ...props }: ComponentProps<'p'>) {
  return <p {...props} className={cn('text-xs leading-5 text-muted-foreground', className)} />
}
export function DataListActions({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} className={cn('ml-auto flex shrink-0 items-center gap-1', className)} />
}
